import { Trans, useLingui } from '@lingui/react/macro'
import { Button, Group, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconChevronLeft } from '@tabler/icons-react'
import { useState } from 'react'
import { BookingSummary } from './BookingSummary'
import { PropertySelector } from './PropertySelector'
import { CITY_LABELS, InfoFormValues, Property } from './types'

interface InfoStepProps {
  initialFirstName: string
  initialLastName: string
  initialPhoneNumber?: string
  properties: Property[]
  city: string
  selectedDate: Date
  selectedTime: Date
  selectedEndTime: Date
  onSubmit: (values: InfoFormValues) => void
  onBack: () => void
  isSubmitting: boolean
}

export function InfoStep({
  initialFirstName,
  initialLastName,
  initialPhoneNumber,
  properties,
  city,
  selectedTime,
  selectedEndTime,
  onSubmit,
  onBack,
  isSubmitting,
}: InfoStepProps) {
  const { t } = useLingui()
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    properties.length > 0 ? String(properties[0].id) : 'other',
  )

  const showAddressFields =
    properties.length === 0 || selectedPropertyId === 'other'

  const form = useForm<InfoFormValues>({
    initialValues: {
      firstName: initialFirstName,
      lastName: initialLastName,
      phoneNumber: initialPhoneNumber || '',
      street: '',
      postalCode: '',
      propertyType: 'detached_house',
      notes: '',
    },
    validate: {
      firstName: (val) => (!val.trim() ? t`First name is required` : null),
      lastName: (val) => (!val.trim() ? t`Last name is required` : null),
      phoneNumber: (val) => {
        if (!val.trim()) return t`Phone number is required`
        if (!/^[\d\s+()-]{7,}$/.test(val)) {
          return t`Invalid phone number`
        }
        return null
      },
      street: (val) =>
        showAddressFields && !val ? t`Address is required` : null,
      postalCode: (val) => {
        if (!showAddressFields) return null
        if (!val) return t`Postal code is required`
        if (!/^\d{5}$/.test(val)) return t`Postal code must be 5 digits`
        return null
      },
      propertyType: (val) =>
        showAddressFields && !val ? t`Property type is required` : null,
    },
  })

  const handleSubmit = (values: InfoFormValues) => {
    const submitValues = {
      ...values,
      propertyId:
        selectedPropertyId && selectedPropertyId !== 'other'
          ? Number(selectedPropertyId)
          : undefined,
    }
    onSubmit(submitValues)
  }

  const cityLabel = CITY_LABELS[city] || city

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <div>
          <Text size="xl" fw={600} mb="xs">
            <Trans>Check details</Trans>
          </Text>
          <Text size="sm" c="dimmed">
            <Trans>We will book the survey visit with these details</Trans>
          </Text>
        </div>

        <BookingSummary
          selectedTime={selectedTime}
          selectedEndTime={selectedEndTime}
          cityLabel={cityLabel}
        />

        <PropertySelector
          properties={properties}
          selectedPropertyId={selectedPropertyId}
          onPropertyChange={setSelectedPropertyId}
          showAddressFields={showAddressFields}
          form={form}
        />

        <Group grow>
          <TextInput
            label={t`First name`}
            placeholder={t`John`}
            required
            autoComplete="given-name"
            {...form.getInputProps('firstName')}
          />
          <TextInput
            label={t`Last name`}
            placeholder={t`Doe`}
            required
            autoComplete="family-name"
            {...form.getInputProps('lastName')}
          />
        </Group>

        <TextInput
          label={t`Phone number`}
          placeholder="050 123 4567"
          required
          autoComplete="tel"
          {...form.getInputProps('phoneNumber')}
        />

        <Textarea
          label={t`Additional information (optional)`}
          placeholder={t`E.g. property age, condition, or other information that may help with the survey visit`}
          rows={3}
          {...form.getInputProps('notes')}
        />

        <Group mt="md">
          <Button
            variant="default"
            onClick={onBack}
            disabled={isSubmitting}
            size="md"
            flex={1}
            leftSection={<IconChevronLeft size={18} />}
          >
            <Trans>Back</Trans>
          </Button>
          <Button type="submit" loading={isSubmitting} size="md" flex={1}>
            <Trans>Book visit</Trans>
          </Button>
        </Group>
      </Stack>
    </form>
  )
}

export type { InfoFormValues }
